import { cn } from '../lib/cn.js';
import { t } from '../i18n/index.js';
import { Button } from './Button.js';
import { IconChevronLeft, IconChevronRight } from './Icon.js';

/**
 * Pagination.
 *
 * ── THE ONE DECISION HERE ─────────────────────────────────────────────────
 *
 * Both references label the buttons "Previous" and "Next". For a ledger that is
 * ambiguous in a way that actually costs the reader something: a general ledger
 * is read oldest-first, so page 2 holds LATER entries — "next" moves forward in
 * time, and a user who reads "next" as "further down the list" is right, while
 * one who reads it as "more recent" is also right. Fine.
 *
 * But a journal is newest-first, where "next" moves BACKWARDS in time. The same
 * word means opposite things two tabs apart.
 *
 * So the labels are overridable and the caller is expected to use words that
 * suit its ordering — "Older"/"Newer" on a ledger, "Previous"/"Next" on a plain
 * list. Defaulting to Previous/Next keeps the common case short.
 */

export function Pagination({
  page,
  pageCount,
  onPage,
  totalItems,
  itemNoun = 'rows',
  previousLabel = t('table.previous'),
  nextLabel = t('table.next'),
  className,
}: {
  /** Zero-based, because every caller's `offset` is `page * limit`. */
  page: number;
  pageCount: number;
  onPage: (next: number) => void;
  /** Total across all pages, so the count is honest about what is not shown. */
  totalItems?: number;
  /**
   * ALREADY PLURAL. "settlements", not "settlement".
   *
   * This used to append an `s` when the count was not 1, and nineteen of the
   * twenty call sites passed a plural noun — so the day book read "105
   * voucherss", Settlements read "38 settlementss", and so on across the
   * product.
   *
   * The append is gone rather than the callers changed, because the singular
   * branch it existed for could never run: this component returns null above
   * when there is one page or fewer, so `totalItems` is always at least two by
   * the time anything is rendered. It was dead code producing a visible defect.
   *
   * A real plural rule belongs in the dictionary (`t` takes `count` and selects
   * a declared form) the day a caller genuinely needs one.
   */
  itemNoun?: string;
  previousLabel?: string;
  nextLabel?: string;
  className?: string;
}) {
  // One page is not pagination. Rendering disabled controls around a single
  // page implies there is more to see.
  if (pageCount <= 1) return null;

  const atStart = page <= 0;
  const atEnd = page >= pageCount - 1;

  return (
    <nav
      aria-label={t('a11y.pagination')}
      className={cn('flex items-center justify-between gap-4 px-(--card-padding) py-3', className)}
    >
      <Button size="sm" disabled={atStart} onClick={() => onPage(page - 1)} leading={<IconChevronLeft />}>
        {previousLabel}
      </Button>

      {/* A status region, so a screen reader user hears where they landed after
          pressing Next rather than having to hunt for it. */}
      <p className="text-sm text-tertiary tnum" role="status" aria-live="polite">
        {t('table.pageOf', { page: page + 1, pages: pageCount })}
        {totalItems !== undefined && (
          <>
            {' · '}
            {totalItems.toLocaleString('en-IN')} {itemNoun}
          </>
        )}
      </p>

      <Button size="sm" disabled={atEnd} onClick={() => onPage(page + 1)} trailing={<IconChevronRight />}>
        {nextLabel}
      </Button>
    </nav>
  );
}
